import { useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Checkbox, App } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogin } from '../../api/auth';
import { useSessionStore } from '../../stores/sessionStore';
import { ApiError } from '../../lib/apiClient';

/** 「记住账号」存的是邮箱（不存密码），下次自动填入 */
const REMEMBER_KEY = 'bom.login.email';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { message } = App.useApp();
  const setSession = useSessionStore((s) => s.setSession);
  const loginMut = useLogin();

  const rememberedEmail = localStorage.getItem(REMEMBER_KEY) ?? '';

  useEffect(() => {
    if (params.get('expired')) message.warning('登录已过期，请重新登录');
  }, [params, message]);

  const onFinish = (values: { email: string; password: string; remember: boolean }) => {
    if (values.remember) localStorage.setItem(REMEMBER_KEY, values.email);
    else localStorage.removeItem(REMEMBER_KEY);

    loginMut.mutate(
      { email: values.email, password: values.password },
      {
        onSuccess: (res) => {
          const token = res.platform_jwt ?? res.session_jwt;
          if (!token) {
            message.error('登录响应异常：缺少令牌');
            return;
          }
          setSession(token, res.expires_at, res.user);
          if (res.user.must_change_password) {
            navigate('/change-password', { replace: true });
          } else if (res.user.is_platform_admin) {
            navigate('/', { replace: true });
          } else {
            navigate('/select-tenant', { replace: true });
          }
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 423) message.error('账号已被锁定，请稍后再试');
            else if (err.code === 'USER_DISABLED') message.error('账号已被停用，请联系管理员');
            else if (err.status === 401) message.error('邮箱或密码错误');
            else message.error(err.message);
          } else {
            message.error('登录失败，请检查网络');
          }
        },
      },
    );
  };

  return (
    <div
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f3f3f3' }}
    >
      <Card style={{ width: 400 }}>
        <Typography.Title level={2} style={{ textAlign: 'center', marginBottom: 28 }}>
          账号登录
        </Typography.Title>
        {params.get('expired') && (
          <Alert type="warning" message="登录已过期" style={{ marginBottom: 16 }} showIcon />
        )}
        <Form
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={{ email: rememberedEmail, remember: !!rememberedEmail }}
        >
          <Form.Item
            name="email"
            label="账号"
            rules={[{ required: true, type: 'email', message: '请输入有效邮箱账号' }]}
          >
            <Input size="large" placeholder="邮箱账号" autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password size="large" placeholder="密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 16 }}>
            <Checkbox>记住账号</Checkbox>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loginMut.isPending}
              className="login-submit"
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
